import React, { useEffect, useRef, useState } from "react";
import { FinetuningService } from "../../services/FinetuningService";
import { DatasetService } from "../../services/DatasetService";
import { FinetuneRunService } from "../../services/FinetuneRunService";
import { usePermissions } from "../../PermissionsProvider";
import { CompositionType, FinetuneDataset, FinetuneRunNew, FinetuneRunStatus, TrainingMethod } from "../../model_gen/finetuning";
import CustomSwitch from "./switch/CustomSwitch";
import RewardFunctionEditor from "./RewardFunctionEditor";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Slider } from "../ui/slider";
import { Tooltip } from "../ui/tooltip";
import { Separator } from "../ui/separator";
import { 
  InfoIcon, 
  PlusIcon, 
  XIcon,
  Loader2Icon,
  SparklesIcon
} from "lucide-react";

interface FinetuneSettingsProps {
  trainingMethod?: TrainingMethod;
  setTrainingMethod?: (value: TrainingMethod) => void;
  compositionType?: CompositionType;
  dpoBeta?: number;
  setDpoBeta?: (value: number) => void;
  grpoNumGenerations?: number;
  setGrpoNumGenerations?: (value: number) => void;
  grpoMaxCompletionLength?: number;
  setGrpoMaxCompletionLength?: (value: number) => void;
  grpoRewardFunction?: string;
  setGrpoRewardFunction?: (value: string) => void;
  grpoTemperature?: number;
  setGrpoTemperature?: (value: number) => void;
  grpoBeta?: number;
  setGrpoBeta?: (value: number) => void;
  grpoStepsPerGeneration?: number;
  setGrpoStepsPerGeneration?: (value: number) => void;
  exportPrompt: string;
  setExportPrompt: (value: string) => void;
  fourbit: boolean;
  setFourbit: (value: boolean) => void;
  isExporting: boolean;
  baseModel: string;
  setBaseModel: (value: string) => void;
  baseFinetuneRunId?: number | null;
  setBaseFinetuneRunId?: (value: number | null) => void;
  useBaseFinetunedModel?: boolean;
  setUseBaseFinetunedModel?: (value: boolean) => void;
  // Used only for filtering finetuned base choices in the portal flow
  baseContextApplicationId?: number;
  baseContextIntentGroupId?: number;
  baseContextIntentId?: number;
  promptDeletions: string[];
  setPromptDeletions: (value: string[]) => void | null;
  warmupSteps: number;
  setWarmupSteps: (value: number) => void;
  warmupRatio: number;
  setWarmupRatio: (value: number) => void;
  learningRate: number;
  setLearningRate: (value: number) => void;
  perDeviceTrainBatchSize?: number;
  setPerDeviceTrainBatchSize?: (value: number) => void;
  gradientAccumulationSteps?: number;
  setGradientAccumulationSteps?: (value: number) => void;
  trainOnResponsesOnly?: boolean;
  setTrainOnResponsesOnly?: (value: boolean) => void;
  lrSchedulerType?: string;
  setLrSchedulerType?: (value: string) => void;
  trainSplit?: number;
  setTrainSplit?: (value: number) => void;
  alpha?: number;
  setAlpha?: (value: number) => void;
  rank?: number;
  setRank?: (value: number) => void;
  epochs?: number;
  setEpochs?: (value: number) => void;
  maxSteps?: number;
  setMaxSteps?: (value: number) => void;
  weightDecay?: number;
  setWeightDecay?: (value: number) => void;
  dropout?: number;
  setDropout?: (value: number) => void;
  rslora?: boolean;
  setRslora?: (value: boolean) => void;
  numGpus?: number;
  setNumGpus?: (value: number) => void;
  hideAdvancedSettings?: boolean;
  showOnlyAdvancedSettings?: boolean;
  intentGroupId?: number | null;
  selectedSubDataset?: FinetuneDataset | null;
  setSelectedSubDataset?: (dataset: FinetuneDataset | null) => void;
  displayName?: string;
  setDisplayName?: (value: string) => void;
  datasetId: number;
  totalSamples?: number;
}

// Helper component for field label (works for both required and optional fields)
const FieldLabel: React.FC<{
  label: string;
  required?: boolean;
  tooltip?: string;
}> = ({ label, required = false, tooltip }) => (
  <div className="flex items-center gap-2 mb-2">
    <Label className="text-sm font-gc-med text-foreground">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </Label>
    {tooltip && (
      <Tooltip content={tooltip}>
        <InfoIcon className="w-4 h-4 text-muted-foreground cursor-help" />
      </Tooltip>
    )}
  </div>
);

const LR_SCHEDULER_OPTIONS: {
  value: string;
  label: string;
  description: string;
  path: string;
}[] = [
  {
    value: "linear",
    label: "Linear Decay",
    description: "Linearly decreases LR to zero",
    path: "M2,4 L46,18",
  },
  {
    value: "cosine",
    label: "Cosine Annealing",
    description: "Smooth cosine curve to zero",
    path: "M2,4 C10,4 16,5 24,10 C32,15 40,18 46,18",
  },
  {
    value: "cosine_with_restarts",
    label: "Cosine with Warm Restarts",
    description: "Cosine decay with periodic resets",
    path: "M2,4 C6,4 9,16 12,16 C12,16 12,4 16,4 C20,4 23,16 26,16 C26,16 26,4 30,4 C34,4 37,16 40,16 C40,16 40,4 44,4 L46,6",
  },
  {
    value: "polynomial",
    label: "Polynomial Decay",
    description: "Polynomial curve to zero",
    path: "M2,4 Q24,6 46,18",
  },
  {
    value: "constant",
    label: "Constant",
    description: "Fixed learning rate throughout",
    path: "M2,6 L46,6",
  },
  {
    value: "inverse_sqrt",
    label: "Inverse Square Root",
    description: "Fast initial decay then flattens",
    path: "M2,4 C6,8 10,12 16,14 C22,16 34,17 46,18",
  },
];

const LrSchedulerOption: React.FC<{ option: (typeof LR_SCHEDULER_OPTIONS)[number] }> = ({
  option,
}) => (
  <div className="flex items-center gap-3 py-0.5">
    <svg
      width="48"
      height="22"
      viewBox="0 0 48 22"
      className="shrink-0"
      aria-hidden
    >
      <path
        d={option.path}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-maitai-green"
      />
    </svg>
    <div className="min-w-0">
      <span className="text-sm font-gc-med">{option.label}</span>
      <span className="text-xs text-muted-foreground ml-2">{option.description}</span>
    </div>
  </div>
);

const FinetuneSettings: React.FC<FinetuneSettingsProps> = ({
  trainingMethod = "SFT",
  setTrainingMethod,
  compositionType,
  dpoBeta,
  setDpoBeta,
  grpoNumGenerations,
  setGrpoNumGenerations,
  grpoMaxCompletionLength,
  setGrpoMaxCompletionLength,
  grpoRewardFunction,
  setGrpoRewardFunction,
  grpoTemperature,
  setGrpoTemperature,
  grpoBeta,
  setGrpoBeta,
  grpoStepsPerGeneration,
  setGrpoStepsPerGeneration,
  exportPrompt,
  setExportPrompt,
  fourbit,
  setFourbit,
  isExporting,
  baseModel,
  setBaseModel,
  baseFinetuneRunId,
  setBaseFinetuneRunId,
  useBaseFinetunedModel = false,
  setUseBaseFinetunedModel,
  baseContextApplicationId,
  baseContextIntentGroupId,
  baseContextIntentId,
  promptDeletions,
  setPromptDeletions,
  warmupSteps,
  setWarmupSteps,
  warmupRatio,
  setWarmupRatio,
  learningRate,
  setLearningRate,
  perDeviceTrainBatchSize,
  setPerDeviceTrainBatchSize,
  gradientAccumulationSteps,
  setGradientAccumulationSteps,
  trainOnResponsesOnly,
  setTrainOnResponsesOnly,
  lrSchedulerType = "linear",
  setLrSchedulerType,
  trainSplit,
  setTrainSplit,
  alpha,
  setAlpha,
  rank,
  setRank,
  epochs,
  setEpochs,
  maxSteps,
  setMaxSteps,
  weightDecay,
  setWeightDecay,
  dropout,
  setDropout,
  rslora,
  setRslora,
  numGpus = 1,
  setNumGpus,
  hideAdvancedSettings = false,
  showOnlyAdvancedSettings = false,
  intentGroupId = null,
  selectedSubDataset = null,
  setSelectedSubDataset,
  datasetId,
  totalSamples,
}) => {
  const { apiKey } = usePermissions();
  const [baseModels, setBaseModels] = useState<string[]>([]);
  const [baseFinetuneRuns, setBaseFinetuneRuns] = useState<FinetuneRunNew[]>([]);
  const [loadingBaseFinetuneRuns, setLoadingBaseFinetuneRuns] = useState<boolean>(false);
  const [datasets, setDatasets] = useState<FinetuneDataset[]>([]);
  const [loadingDatasets, setLoadingDatasets] = useState<boolean>(false);
  const [promptLoading, setPromptLoading] = useState<boolean>(false);

  // Filter finetune runs to the current "context" when possible.
  // If a run lacks the context fields (older runs), we keep it rather than hiding it.
  const filteredBaseFinetuneRuns = baseFinetuneRuns.filter((run) => {
    if (
      baseContextIntentId &&
      run.intent_id !== null &&
      run.intent_id !== undefined &&
      run.intent_id !== baseContextIntentId
    ) {
      return false;
    }
    if (
      baseContextIntentGroupId &&
      run.intent_group_id !== null &&
      run.intent_group_id !== undefined &&
      run.intent_group_id !== baseContextIntentGroupId
    ) {
      return false;
    }
    if (
      baseContextApplicationId &&
      run.application_id !== null &&
      run.application_id !== undefined &&
      run.application_id !== baseContextApplicationId
    ) {
      return false;
    }
    return true;
  });
  const baseFinetuneRunsToShow = filteredBaseFinetuneRuns;

  // Input field raw text values
  const [warmupRatioText, setWarmupRatioText] = useState<string>(
    warmupRatio?.toString() || ""
  );
  const [learningRateText, setLearningRateText] = useState<string>(
    learningRate?.toString() || ""
  );
  const [warmupStepsText, setWarmupStepsText] = useState<string>(
    warmupSteps?.toString() || ""
  );
  const [perDeviceTrainBatchSizeText, setPerDeviceTrainBatchSizeText] =
    useState<string>(perDeviceTrainBatchSize?.toString() || "");
  const [gradientAccumulationStepsText, setGradientAccumulationStepsText] =
    useState<string>(gradientAccumulationSteps?.toString() || "");
  const [alphaText, setAlphaText] = useState<string>(alpha?.toString() || "");
  const [rankText, setRankText] = useState<string>(rank?.toString() || "");
  const [epochsText, setEpochsText] = useState<string>(
    epochs?.toString() || ""
  );
  const [maxStepsText, setMaxStepsText] = useState<string>(
    maxSteps && maxSteps > 0 ? maxSteps.toString() : ""
  );
  const [durationMode, setDurationMode] = useState<"epochs" | "max_steps">(
    maxSteps && maxSteps > 0 ? "max_steps" : "epochs"
  );
  const [weightDecayText, setWeightDecayText] = useState<string>(
    (weightDecay ?? 0).toString()
  );
  const [dropoutText, setDropoutText] = useState<string>(
    dropout !== undefined && dropout !== null ? dropout.toString() : "0.05"
  );
  const [dpoBetaText, setDpoBetaText] = useState<string>(
    dpoBeta !== undefined && dpoBeta !== null ? dpoBeta.toString() : "0.1"
  );
  const [grpoNumGenerationsText, setGrpoNumGenerationsText] = useState<string>(
    grpoNumGenerations !== undefined && grpoNumGenerations !== null ? grpoNumGenerations.toString() : "4"
  );
  const [grpoMaxCompletionLengthText, setGrpoMaxCompletionLengthText] = useState<string>(
    grpoMaxCompletionLength !== undefined && grpoMaxCompletionLength !== null ? grpoMaxCompletionLength.toString() : "512"
  );
  const [grpoTemperatureText, setGrpoTemperatureText] = useState<string>(
    grpoTemperature !== undefined && grpoTemperature !== null ? grpoTemperature.toString() : "1.0"
  );
  const [grpoBetaText, setGrpoBetaText] = useState<string>(
    grpoBeta !== undefined && grpoBeta !== null ? grpoBeta.toString() : "0.0"
  );
  const [grpoStepsPerGenerationText, setGrpoStepsPerGenerationText] = useState<string>(
    grpoStepsPerGeneration !== undefined && grpoStepsPerGeneration !== null ? grpoStepsPerGeneration.toString() : "1"
  );

  useEffect(() => { setWarmupRatioText(warmupRatio?.toString() || ""); }, [warmupRatio]);
  useEffect(() => { setLearningRateText(learningRate?.toString() || ""); }, [learningRate]);
  useEffect(() => { setWarmupStepsText(warmupSteps?.toString() || ""); }, [warmupSteps]);
  useEffect(() => { setPerDeviceTrainBatchSizeText(perDeviceTrainBatchSize?.toString() || ""); }, [perDeviceTrainBatchSize]);
  useEffect(() => { setGradientAccumulationStepsText(gradientAccumulationSteps?.toString() || ""); }, [gradientAccumulationSteps]);
  useEffect(() => { setAlphaText(alpha?.toString() || ""); }, [alpha]);
  useEffect(() => { setRankText(rank?.toString() || ""); }, [rank]);
  useEffect(() => { setEpochsText(epochs?.toString() || ""); }, [epochs]);
  useEffect(() => { setMaxStepsText(maxSteps && maxSteps > 0 ? maxSteps.toString() : ""); }, [maxSteps]);
  useEffect(() => { setWeightDecayText((weightDecay ?? 0).toString()); }, [weightDecay]);
  useEffect(() => { setDropoutText(dropout !== undefined && dropout !== null ? dropout.toString() : "0.05"); }, [dropout]);
  useEffect(() => { setDpoBetaText(dpoBeta !== undefined && dpoBeta !== null ? dpoBeta.toString() : "0.1"); }, [dpoBeta]);
  useEffect(() => { setGrpoNumGenerationsText(grpoNumGenerations !== undefined && grpoNumGenerations !== null ? grpoNumGenerations.toString() : "4"); }, [grpoNumGenerations]);
  useEffect(() => { setGrpoMaxCompletionLengthText(grpoMaxCompletionLength !== undefined && grpoMaxCompletionLength !== null ? grpoMaxCompletionLength.toString() : "512"); }, [grpoMaxCompletionLength]);
  useEffect(() => { setGrpoTemperatureText(grpoTemperature !== undefined && grpoTemperature !== null ? grpoTemperature.toString() : "1.0"); }, [grpoTemperature]);
  useEffect(() => { setGrpoBetaText(grpoBeta !== undefined && grpoBeta !== null ? grpoBeta.toString() : "0.0"); }, [grpoBeta]);
  useEffect(() => { setGrpoStepsPerGenerationText(grpoStepsPerGeneration !== undefined && grpoStepsPerGeneration !== null ? grpoStepsPerGeneration.toString() : "1"); }, [grpoStepsPerGeneration]);

  const getPrompt = () => {
    let selectedDatasetId;
    if (selectedSubDataset) {
      selectedDatasetId = selectedSubDataset.id;
    } else {
      selectedDatasetId = datasetId;
    }

    DatasetService.getDatasetPrompt(
      selectedDatasetId,
      setPromptLoading,
      apiKey
    ).subscribe(
      (prompt: { prompt: string }) => {
        setExportPrompt(prompt.prompt);
      },
      (error: any) => {
        console.error("Failed to fetch prompt:", error);
      }
    );
  };

  const baseModelRef = useRef(baseModel);
  baseModelRef.current = baseModel;
  const useBaseFinetunedModelRef = useRef(useBaseFinetunedModel);
  useBaseFinetunedModelRef.current = useBaseFinetunedModel;

  // Fetch base models
  useEffect(() => {
    if (!showOnlyAdvancedSettings) {
      FinetuningService.getBaseModels(() => {}, apiKey).subscribe(
        (models: string[]) => {
          const uniqueModels = models.filter(
            (m, i, arr) => arr.indexOf(m) === i
          );
          setBaseModels(uniqueModels);
          if (uniqueModels.length > 0 && !baseModelRef.current && !useBaseFinetunedModelRef.current) {
            setBaseModel(uniqueModels[0]);
          }
        },
        (error: any) => {
          console.error("Failed to fetch base models:", error);
        }
      );
    }
  }, [apiKey, showOnlyAdvancedSettings]); // intentionally excludes baseModel to avoid re-fetch loops

  // Fetch completed/validating finetune runs to allow "finetune on top" of an existing model.
  useEffect(() => {
    if (showOnlyAdvancedSettings) return;
    if (!setBaseFinetuneRunId) return;
    if (!useBaseFinetunedModel) return;

    const sub = FinetuneRunService.getFinetuneRunOptions(
      0,
      200,
      setLoadingBaseFinetuneRuns,
      apiKey,
      ["COMPLETED", "VALIDATING"] as FinetuneRunStatus[]
    ).subscribe(
      (resp: any) => {
        setBaseFinetuneRuns(resp?.items || []);
      },
      (error: any) => {
        console.error("Failed to fetch base finetune runs:", error);
        setBaseFinetuneRuns([]);
      }
    );
    return () => sub.unsubscribe();
  }, [apiKey, showOnlyAdvancedSettings, setBaseFinetuneRunId, useBaseFinetunedModel]);

  // If a base finetune run is selected (e.g. from persisted localStorage), keep baseModel in sync.
  useEffect(() => {
    if (!baseFinetuneRunId) return;
    const selected = baseFinetuneRuns.find((r) => r.id === baseFinetuneRunId);
    if (selected?.base_model && selected.base_model !== baseModel) {
      setBaseModel(selected.base_model);
    }
  }, [baseFinetuneRunId, baseFinetuneRuns, baseModel, setBaseModel]);

  // Fetch datasets for the intent group
  useEffect(() => {
    if (intentGroupId) {
      setLoadingDatasets(true);
      DatasetService.getDatasetsByIntentGroup(
        intentGroupId,
        0,
        100,
        setLoadingDatasets,
        apiKey
      ).subscribe(
        (response: any) => {
          if (response && response.items) {
            setDatasets(
              response.items.filter((d: any) => d?.meta?.used_in_test !== true)
            );
          } else {
            setDatasets([]);
          }
        },
        (error: any) => {
          console.error("Failed to fetch datasets:", error);
          setDatasets([]);
          setLoadingDatasets(false);
        }
      );
    }
  }, [intentGroupId, apiKey]);

  // Helper to format dataset label
  const formatDatasetLabel = (dataset: FinetuneDataset) => {
    let sourceName = "";
    if (dataset.application_name && dataset.intent_name) {
      sourceName = `${dataset.application_name} / ${dataset.intent_name}`;
    } else if (dataset.application_name) {
      sourceName = dataset.application_name;
    } else if (dataset.intent_name) {
      sourceName = dataset.intent_name;
    } else if (dataset.intent_group_name) {
      sourceName = dataset.intent_group_name;
    } else {
      sourceName = "Unknown source";
    }

    return `${dataset.name} - ${sourceName}`;
  };

  // Handle dataset selection change
  const handleDatasetChange = (value: string) => {
    if (setSelectedSubDataset) {
      if (value) {
        const selectedDataset = datasets.find(
          (dataset) => dataset.id?.toString() === value
        );
        setSelectedSubDataset(selectedDataset || null);
      } else {
        setSelectedSubDataset(null);
      }
    }
  };

  return (
    <div className="space-y-6">
      {showOnlyAdvancedSettings && (
        <>
          {/* Only show Sub Dataset if intentGroupId is not null */}
          {intentGroupId && (
            <div className="space-y-3">
              <FieldLabel
                label="Sub Dataset"
                tooltip="Optional. If set, this will target training with a second finetune. Leave empty for standard finetuning."
              />
              <Select
                value={selectedSubDataset?.id?.toString() || ""}
                onValueChange={handleDatasetChange}
                disabled={isExporting}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a sub dataset" />
                </SelectTrigger>
                <SelectContent>
                  {loadingDatasets ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2Icon className="w-4 h-4 animate-spin" />
                      <span className="ml-2 text-sm">Loading datasets...</span>
                    </div>
                  ) : datasets.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground">No datasets found</div>
                  ) : (
                    datasets.map((dataset) => (
                      <SelectItem key={dataset.id} value={dataset.id?.toString() || ""}>
                        {formatDatasetLabel(dataset)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-8">
            {/* Row 1: Optimizer */}
            <div>
              <Label className="text-xs font-gc-med text-muted-foreground uppercase tracking-wide mb-4 block">Optimizer</Label>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label className="text-sm font-gc-med text-foreground">Warmup Ratio</Label>
                    <Input
                      type="text"
                      value={warmupRatioText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setWarmupRatioText(value);
                        if (value === "") {
                          setWarmupRatio(0);
                        } else if (!isNaN(parseFloat(value))) {
                          setWarmupRatio(parseFloat(value));
                        }
                      }}
                      disabled={isExporting}
                      placeholder="0.03"
                    />
                  </div>
                  <div className="space-y-3">
                    <Label className="text-sm font-gc-med text-foreground">Learning Rate</Label>
                    <Input
                      type="text"
                      value={learningRateText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setLearningRateText(value);
                        if (value === "") {
                          setLearningRate(0);
                        } else if (!isNaN(parseFloat(value))) {
                          setLearningRate(parseFloat(value));
                        }
                      }}
                      disabled={isExporting}
                      placeholder="1e-4"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">LR Schedule</Label>
                  <Select
                    value={lrSchedulerType}
                    onValueChange={(value) => setLrSchedulerType?.(value)}
                    disabled={isExporting}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LR_SCHEDULER_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value} textValue={opt.label}>
                          <LrSchedulerOption option={opt} />
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Row 2: Batch & Compute */}
            <div>
              <Label className="text-xs font-gc-med text-muted-foreground uppercase tracking-wide mb-4 block">Batch & Compute</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">Batch Size</Label>
                <Input
                  type="text"
                  value={perDeviceTrainBatchSizeText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPerDeviceTrainBatchSizeText(value);
                    if (value === "") {
                      setPerDeviceTrainBatchSize?.(0);
                    } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                      setPerDeviceTrainBatchSize?.(parseInt(value));
                    }
                  }}
                  disabled={isExporting}
                  placeholder="8"
                />
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">Gradient Accumulation</Label>
                <Input
                  type="text"
                  value={gradientAccumulationStepsText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setGradientAccumulationStepsText(value);
                    if (value === "") {
                      setGradientAccumulationSteps?.(0);
                    } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                      setGradientAccumulationSteps?.(parseInt(value));
                    }
                  }}
                  disabled={isExporting}
                  placeholder="4"
                />
              </div>

              {setNumGpus && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">GPUs</Label>
                  <Select
                    value={numGpus.toString()}
                    onValueChange={(value) => setNumGpus(parseInt(value))}
                    disabled={isExporting}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1</SelectItem>
                      <SelectItem value="4">4</SelectItem>
                      <SelectItem value="8">8</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              </div>
            </div>

            <Separator />

            {/* Row 3: LoRA Settings */}
            <div>
              <Label className="text-xs font-gc-med text-muted-foreground uppercase tracking-wide mb-4 block">LoRA Settings</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {setRank && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Rank</Label>
                  <Input
                    type="text"
                    value={rankText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRankText(value);
                      if (value === "") {
                        setRank(0);
                      } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                        setRank(parseInt(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="16"
                  />
                </div>
              )}

              {setAlpha && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Alpha</Label>
                  <Input
                    type="text"
                    value={alphaText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setAlphaText(value);
                      if (value === "") {
                        setAlpha(0);
                      } else if (!isNaN(parseFloat(value))) {
                        setAlpha(parseFloat(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="16"
                  />
                </div>
              )}

              {setRslora && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">RSLoRA</Label>
                  <div className="h-10 flex items-center">
                    <CustomSwitch
                      checked={rslora}
                      onChange={(checked) => setRslora(checked)}
                      disabled={isExporting}
                    />
                  </div>
                </div>
              )}
              </div>
            </div>

            <Separator />

            {/* Row 4: Regularization & Quantization */}
            <div>
              <Label className="text-xs font-gc-med text-muted-foreground uppercase tracking-wide mb-4 block">Regularization & Quantization</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {setDropout && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Dropout</Label>
                  <Input
                    type="text"
                    value={dropoutText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDropoutText(value);
                      if (value === "") {
                        setDropout(0);
                      } else if (!isNaN(parseFloat(value))) {
                        setDropout(parseFloat(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="0.05"
                  />
                </div>
              )}

              {setWeightDecay && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Weight Decay</Label>
                  <Input
                    type="text"
                    value={weightDecayText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setWeightDecayText(value);
                      if (value === "") {
                        setWeightDecay(0);
                      } else if (!isNaN(parseFloat(value))) {
                        setWeightDecay(parseFloat(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="0"
                  />
                </div>
              )}

              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">4-bit Quantization</Label>
                <div className="h-10 flex items-center">
                  <CustomSwitch
                    checked={fourbit}
                    onChange={(checked) => setFourbit(checked)}
                    disabled={isExporting}
                  />
                </div>
              </div>
              </div>
            </div>

            <Separator />

            {/* Row 5: Training Duration */}
            <div>
              <Label className="text-xs font-gc-med text-muted-foreground uppercase tracking-wide mb-4 block">Training Duration</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {setEpochs && (
                <div className="space-y-3">
                  <div className="inline-flex rounded-md border border-border overflow-hidden">
                    <button
                      type="button"
                      className={`text-xs font-gc-med px-3 py-1 transition-colors ${durationMode === "epochs" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-foreground"}`}
                      onClick={() => {
                        setDurationMode("epochs");
                        setMaxSteps?.(0);
                        setMaxStepsText("");
                      }}
                      disabled={isExporting}
                    >
                      Epochs
                    </button>
                    <button
                      type="button"
                      className={`text-xs font-gc-med px-3 py-1 border-l border-border transition-colors ${durationMode === "max_steps" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-foreground"}`}
                      onClick={() => {
                        setDurationMode("max_steps");
                        setEpochs(0);
                        setEpochsText("");
                      }}
                      disabled={isExporting}
                    >
                      Max Steps
                    </button>
                  </div>
                  {durationMode === "epochs" ? (
                    <Input
                      type="text"
                      value={epochsText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEpochsText(value);
                        if (value === "") {
                          setEpochs(0);
                        } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                          setEpochs(parseInt(value));
                        }
                      }}
                      disabled={isExporting}
                      placeholder="1"
                    />
                  ) : (
                    <Input
                      type="text"
                      value={maxStepsText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setMaxStepsText(value);
                        if (value === "") {
                          setMaxSteps?.(0);
                        } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                          setMaxSteps?.(parseInt(value));
                        }
                      }}
                      disabled={isExporting}
                      placeholder="1000"
                    />
                  )}
                </div>
              )}

              {trainingMethod === "SFT" && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Train on Responses Only</Label>
                  <div className="h-10 flex items-center">
                    <CustomSwitch
                      checked={trainOnResponsesOnly}
                      onChange={(checked) => setTrainOnResponsesOnly?.(checked)}
                      disabled={isExporting}
                    />
                  </div>
                </div>
              )}
              </div>
            </div>

            {/* Prompt Deletions — full width */}
            {trainingMethod === "SFT" && (
              <div className="space-y-3">
                <FieldLabel label="Prompt Deletions" />
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    onClick={() => setPromptDeletions([...promptDeletions, ""])}
                    disabled={isExporting || !setPromptDeletions}
                    className="gap-2"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add Deletion
                  </Button>

                  {promptDeletions &&
                    promptDeletions.map((deletion, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          value={deletion}
                          onChange={(e) => {
                            if (setPromptDeletions) {
                              const newDeletions = [...promptDeletions];
                              newDeletions[index] = e.target.value;
                              setPromptDeletions(newDeletions);
                            }
                          }}
                          disabled={isExporting || !setPromptDeletions}
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (setPromptDeletions) {
                              setPromptDeletions(
                                promptDeletions.filter((_, i) => i !== index)
                              );
                            }
                          }}
                          disabled={isExporting || !setPromptDeletions}
                          className="gap-1"
                        >
                          <XIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

        </>
      )}

      {!showOnlyAdvancedSettings && (
        <>
          {trainingMethod === "DPO" && setDpoBeta && (
            <div className="space-y-3">
              <FieldLabel
                label="Beta"
                tooltip="KL penalty strength for DPO. Higher values produce outputs closer to the reference model."
              />
              <Input
                type="text"
                value={dpoBetaText}
                onChange={(e) => {
                  const value = e.target.value;
                  setDpoBetaText(value);
                  if (value === "") {
                    setDpoBeta(0.1);
                  } else if (!isNaN(parseFloat(value))) {
                    setDpoBeta(parseFloat(value));
                  }
                }}
                disabled={isExporting}
                placeholder="0.1"
              />
            </div>
          )}

          {trainingMethod === "GRPO" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {setGrpoNumGenerations && (
                  <div className="space-y-3">
                    <FieldLabel
                      label="Num Generations"
                      tooltip="Number of completions generated per prompt for reward comparison. Higher values improve signal but increase compute."
                    />
                    <Input
                      type="text"
                      value={grpoNumGenerationsText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setGrpoNumGenerationsText(value);
                        if (value === "") {
                          setGrpoNumGenerations(4);
                        } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                          setGrpoNumGenerations(parseInt(value));
                        }
                      }}
                      disabled={isExporting}
                      placeholder="4"
                    />
                  </div>
                )}
                {setGrpoMaxCompletionLength && (
                  <div className="space-y-3">
                    <FieldLabel
                      label="Max Completion Length"
                      tooltip="Maximum token length for generated completions during GRPO training."
                    />
                    <Input
                      type="text"
                      value={grpoMaxCompletionLengthText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setGrpoMaxCompletionLengthText(value);
                        if (value === "") {
                          setGrpoMaxCompletionLength(512);
                        } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                          setGrpoMaxCompletionLength(parseInt(value));
                        }
                      }}
                      disabled={isExporting}
                      placeholder="512"
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {setGrpoTemperature && (
                  <div className="space-y-3">
                    <FieldLabel
                      label="Temperature"
                      tooltip="Sampling temperature for generation. Higher values (1.5-2.0) increase diversity and help prevent model collapse."
                    />
                    <Input
                      type="text"
                      value={grpoTemperatureText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setGrpoTemperatureText(value);
                        if (value === "") {
                          setGrpoTemperature(1.0);
                        } else if (!isNaN(parseFloat(value))) {
                          setGrpoTemperature(parseFloat(value));
                        }
                      }}
                      disabled={isExporting}
                      placeholder="1.0"
                    />
                  </div>
                )}
                {setGrpoBeta && (
                  <div className="space-y-3">
                    <FieldLabel
                      label="KL Penalty (Beta)"
                      tooltip="Penalizes divergence from the reference policy. Prevents collapse by keeping the model close to SFT baseline. 0 = disabled."
                    />
                    <Input
                      type="text"
                      value={grpoBetaText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setGrpoBetaText(value);
                        if (value === "") {
                          setGrpoBeta(0.0);
                        } else if (!isNaN(parseFloat(value))) {
                          setGrpoBeta(parseFloat(value));
                        }
                      }}
                      disabled={isExporting}
                      placeholder="0.0"
                    />
                  </div>
                )}
                {setGrpoStepsPerGeneration && (
                  <div className="space-y-3">
                    <FieldLabel
                      label="Steps per Generation"
                      tooltip="Reuse generated completions for N optimizer steps before re-generating. Higher values (2-4) dramatically speed up training."
                    />
                    <Input
                      type="text"
                      value={grpoStepsPerGenerationText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setGrpoStepsPerGenerationText(value);
                        if (value === "") {
                          setGrpoStepsPerGeneration(1);
                        } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                          setGrpoStepsPerGeneration(parseInt(value));
                        }
                      }}
                      disabled={isExporting}
                      placeholder="1"
                    />
                  </div>
                )}
              </div>

              {setGrpoRewardFunction && (
                <RewardFunctionEditor
                  value={grpoRewardFunction || ""}
                  onChange={setGrpoRewardFunction}
                  disabled={isExporting}
                />
              )}
            </div>
          )}

          {trainingMethod === "SFT" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <FieldLabel
                label="Prompt"
                tooltip="Optional prompt for the finetune"
              />
              <Button
                variant="outline"
                onClick={getPrompt}
                disabled={promptLoading || isExporting}
                className="gap-2"
              >
                {promptLoading ? (
                  <Loader2Icon className="w-4 h-4 animate-spin" />
                ) : (
                  <SparklesIcon className="w-4 h-4" />
                )}
                Suggest a Prompt
              </Button>
            </div>
            <textarea
              placeholder="Enter prompt (optional)"
              value={exportPrompt}
              onChange={(e) => setExportPrompt(e.target.value)}
              rows={4}
              disabled={isExporting}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          )}

          {/* Base selection: one toggle + one dropdown that switches options */}
          {setBaseFinetuneRunId && setUseBaseFinetunedModel ? (
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <FieldLabel
                    label="Base Model"
                    required
                    tooltip="Toggle to switch between starting from a base model or continuing from an existing finetuned model."
                  />
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <span
                    className={
                      useBaseFinetunedModel
                        ? "text-sm text-muted-foreground"
                        : "text-sm text-foreground"
                    }
                  >
                    Base Model
                  </span>
                  <CustomSwitch
                    checked={useBaseFinetunedModel}
                    onChange={(checked) => {
                      setUseBaseFinetunedModel(checked);
                      // Always clear the finetuned selection when switching modes.
                      setBaseFinetuneRunId(null);
                      if (!checked && !baseModel && baseModels.length > 0) {
                        setBaseModel(baseModels[0]);
                      }
                    }}
                    disabled={isExporting}
                  />
                  <span
                    className={
                      useBaseFinetunedModel
                        ? "text-sm text-foreground"
                        : "text-sm text-muted-foreground"
                    }
                  >
                    Finetuned Model
                  </span>
                </div>
              </div>

              <Select
                value={
                  useBaseFinetunedModel
                    ? baseFinetuneRunId
                      ? baseFinetuneRunId.toString()
                      : ""
                    : baseModel
                }
                onValueChange={(value) => {
                  if (useBaseFinetunedModel) {
                    const parsed = parseInt(value);
                    if (!isNaN(parsed)) {
                      setBaseFinetuneRunId?.(parsed);
                      const selected = baseFinetuneRunsToShow.find((r) => r.id === parsed);
                      if (selected?.base_model) {
                        setBaseModel(selected.base_model);
                      }
                    }
                  } else {
                    setBaseModel(value);
                  }
                }}
                disabled={isExporting}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      useBaseFinetunedModel
                        ? "Select a finetuned model"
                        : "Select base model"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {useBaseFinetunedModel ? (
                    loadingBaseFinetuneRuns ? (
                      <SelectItem value="__loading" disabled>
                        Loading finetuned models...
                      </SelectItem>
                    ) : baseFinetuneRunsToShow.length === 0 ? (
                      <SelectItem value="__empty" disabled>
                        No completed finetune runs found
                      </SelectItem>
                    ) : (
                      baseFinetuneRunsToShow.map((run) => (
                        <SelectItem key={run.id} value={run.id.toString()}>
                          {run.display_name}
                        </SelectItem>
                      ))
                    )
                  ) : (
                    baseModels.map((model) => (
                      <SelectItem key={model} value={model}>
                        {model}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-3">
              <FieldLabel label="Base Model" required />
              <Select value={baseModel} onValueChange={setBaseModel} disabled={isExporting}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select base model" />
                </SelectTrigger>
                <SelectContent>
                  {baseModels.map((model) => (
                    <SelectItem key={model} value={model}>
                      {model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {setTrainSplit && (
            <div className="space-y-2">
              <FieldLabel
                label="Train Split"
                tooltip="Percentage of data to use for training (remainder used for evaluation)"
              />
              <div className="flex items-center gap-3">
                <Slider
                  value={[trainSplit || 0.8]}
                  onValueChange={(value) => setTrainSplit(value[0])}
                  min={0.5}
                  max={1}
                  step={0.01}
                  disabled={isExporting}
                  className="flex-1"
                />
                <div className="flex items-center gap-1 shrink-0">
                  <Input
                    type="text"
                    value={Math.round((trainSplit || 0.8) * 100).toString()}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9]/g, "");
                      if (raw === "") return;
                      const pct = Math.min(100, Math.max(50, parseInt(raw)));
                      setTrainSplit(pct / 100);
                    }}
                    disabled={isExporting}
                    className="w-16 text-center"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              </div>
              {totalSamples != null && totalSamples > 0 && (
                <p className="text-xs text-muted-foreground">
                  {Math.round(totalSamples * (trainSplit || 0.8)).toLocaleString()} train / {Math.round(totalSamples * (1 - (trainSplit || 0.8))).toLocaleString()} eval of {totalSamples.toLocaleString()} total
                </p>
              )}
            </div>
          )}
        </>
      )}

      {!hideAdvancedSettings && !showOnlyAdvancedSettings && (
        <>
          {/* Only show Sub Dataset if intentGroupId is not null */}
          {intentGroupId && (
            <div className="space-y-3">
              <FieldLabel
                label="Sub Dataset"
                tooltip="Optional. If set, this will target training with a second finetune. Leave empty for standard finetuning."
              />
              <Select
                value={selectedSubDataset?.id?.toString() || ""}
                onValueChange={handleDatasetChange}
                disabled={isExporting}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a sub dataset" />
                </SelectTrigger>
                <SelectContent>
                  {loadingDatasets ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2Icon className="w-4 h-4 animate-spin" />
                      <span className="ml-2 text-sm">Loading datasets...</span>
                    </div>
                  ) : datasets.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground">No datasets found</div>
                  ) : (
                    datasets.map((dataset) => (
                      <SelectItem key={dataset.id} value={dataset.id?.toString() || ""}>
                        {formatDatasetLabel(dataset)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-3">
            <FieldLabel
              label="4-bit Quantization"
              tooltip="Use 4-bit quantization for finetuning"
            />
            <div className="flex items-center">
              <CustomSwitch
                checked={fourbit}
                onChange={(checked) => setFourbit(checked)}
                disabled={isExporting}
              />
            </div>
          </div>

          {trainingMethod === "SFT" && (
          <div className="space-y-3">
            <FieldLabel label="Prompt Deletions" />
            <div className="space-y-3">
              <Button
                variant="outline"
                onClick={() => setPromptDeletions([...promptDeletions, ""])}
                disabled={isExporting || !setPromptDeletions}
                className="gap-2"
              >
                <PlusIcon className="w-4 h-4" />
                Add Deletion
              </Button>

              {promptDeletions &&
                promptDeletions.map((deletion, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={deletion}
                      onChange={(e) => {
                        if (setPromptDeletions) {
                          const newDeletions = [...promptDeletions];
                          newDeletions[index] = e.target.value;
                          setPromptDeletions(newDeletions);
                        }
                      }}
                      disabled={isExporting || !setPromptDeletions}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (setPromptDeletions) {
                          setPromptDeletions(
                            promptDeletions.filter((_, i) => i !== index)
                          );
                        }
                      }}
                      disabled={isExporting || !setPromptDeletions}
                      className="gap-1"
                    >
                      <XIcon className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
            </div>
          </div>
          )}

          <Separator className="my-6" />

          <div className="space-y-6">
            <FieldLabel
              label="Training Parameters"
              tooltip="Configure model training parameters"
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">Warmup Steps</Label>
                <Input
                  type="text"
                  value={warmupStepsText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setWarmupStepsText(value);
                    if (value === "") {
                      setWarmupSteps(0);
                    } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                      setWarmupSteps(parseInt(value));
                    }
                  }}
                  disabled={isExporting}
                  placeholder="5"
                />
              </div>
              
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">Learning Rate</Label>
                <Input
                  type="text"
                  value={learningRateText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLearningRateText(value);
                    if (value === "") {
                      setLearningRate(0);
                    } else if (!isNaN(parseFloat(value))) {
                      setLearningRate(parseFloat(value));
                    }
                  }}
                  disabled={isExporting}
                  placeholder="1e-4"
                />
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">LR Schedule</Label>
                <Select
                  value={lrSchedulerType}
                  onValueChange={(value) => setLrSchedulerType?.(value)}
                  disabled={isExporting}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LR_SCHEDULER_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} textValue={opt.label}>
                        <LrSchedulerOption option={opt} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">Per Device Train Batch Size</Label>
                <Input
                  type="text"
                  value={perDeviceTrainBatchSizeText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPerDeviceTrainBatchSizeText(value);
                    if (value === "") {
                      setPerDeviceTrainBatchSize?.(0);
                    } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                      setPerDeviceTrainBatchSize?.(parseInt(value));
                    }
                  }}
                  disabled={isExporting}
                  placeholder="8"
                />
              </div>
              
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">Gradient Accumulation Steps</Label>
                <Input
                  type="text"
                  value={gradientAccumulationStepsText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setGradientAccumulationStepsText(value);
                    if (value === "") {
                      setGradientAccumulationSteps?.(0);
                    } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                      setGradientAccumulationSteps?.(parseInt(value));
                    }
                  }}
                  disabled={isExporting}
                  placeholder="4"
                />
              </div>
              
              {setAlpha && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Alpha</Label>
                  <Input
                    type="text"
                    value={alphaText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setAlphaText(value);
                      if (value === "") {
                        setAlpha(0);
                      } else if (!isNaN(parseFloat(value))) {
                        setAlpha(parseFloat(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="16"
                  />
                </div>
              )}
              
              {setRank && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Rank</Label>
                  <Input
                    type="text"
                    value={rankText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRankText(value);
                      if (value === "") {
                        setRank(0);
                      } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                        setRank(parseInt(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="16"
                  />
                </div>
              )}
              
              {setEpochs && (
                <div className="space-y-3">
                  <div className="inline-flex rounded-md border border-border overflow-hidden">
                    <button
                      type="button"
                      className={`text-xs font-gc-med px-3 py-1 transition-colors ${durationMode === "epochs" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-foreground"}`}
                      onClick={() => {
                        setDurationMode("epochs");
                        setMaxSteps?.(0);
                        setMaxStepsText("");
                      }}
                      disabled={isExporting}
                    >
                      Epochs
                    </button>
                    <button
                      type="button"
                      className={`text-xs font-gc-med px-3 py-1 border-l border-border transition-colors ${durationMode === "max_steps" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-foreground"}`}
                      onClick={() => {
                        setDurationMode("max_steps");
                        setEpochs(0);
                        setEpochsText("");
                      }}
                      disabled={isExporting}
                    >
                      Max Steps
                    </button>
                  </div>
                  {durationMode === "epochs" ? (
                    <Input
                      type="text"
                      value={epochsText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEpochsText(value);
                        if (value === "") {
                          setEpochs(0);
                        } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                          setEpochs(parseInt(value));
                        }
                      }}
                      disabled={isExporting}
                      placeholder="1"
                    />
                  ) : (
                    <Input
                      type="text"
                      value={maxStepsText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setMaxStepsText(value);
                        if (value === "") {
                          setMaxSteps?.(0);
                        } else if (!isNaN(parseInt(value)) && /^\d*$/.test(value)) {
                          setMaxSteps?.(parseInt(value));
                        }
                      }}
                      disabled={isExporting}
                      placeholder="1000"
                    />
                  )}
                </div>
              )}
              
              {setWeightDecay && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Weight Decay</Label>
                  <Input
                    type="text"
                    value={weightDecayText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setWeightDecayText(value);
                      if (value === "") {
                        setWeightDecay(0);
                      } else if (!isNaN(parseFloat(value))) {
                        setWeightDecay(parseFloat(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="0"
                  />
                </div>
              )}
              
              {setDropout && (
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">Dropout</Label>
                  <Input
                    type="text"
                    value={dropoutText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDropoutText(value);
                      if (value === "") {
                        setDropout(0);
                      } else if (!isNaN(parseFloat(value))) {
                        setDropout(parseFloat(value));
                      }
                    }}
                    disabled={isExporting}
                    placeholder="0.05"
                  />
                </div>
              )}
            </div>
            
            {setRslora && (
              <div className="space-y-3">
                <FieldLabel label="RSLoRA" />
                <div className="flex items-center">
                  <CustomSwitch
                    checked={rslora}
                    onChange={(checked) => setRslora(checked)}
                    disabled={isExporting}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FinetuneSettings;


